/**
 * ShapeSvg — renders a shape as an inline SVG path element.
 * Useful for decorative shapes, loading skeletons, and outlines.
 * Supports morphed rendering via a `progress` prop.
 *
 * @example
 * // Static shape
 * <ShapeSvg shape="sunny" width={200} height={200} fill="#6750A4" />
 *
 * @example
 * // Morphed shape (controlled externally)
 * <ShapeSvg shape="circle" morphTo="flower" progress={0.5} width={200} height={200} />
 */

import { useMemo } from "react";
import { resolveShape } from "../../shapes/catalog/md3-expressive-shapes";
import { Morph } from "../../shapes/morph/morph";
import { toSvgPath } from "../../shapes/render/shape-rendering";
import type { ShapeSvgProps } from "./types";

/**
 * ShapeSvg renders a shape or morphed shape as a plain SVG path.
 */
export function ShapeSvg({
	shape,
	morphTo,
	progress = 0,
	width = 100,
	height = 100,
	fill = "currentColor",
	stroke = "none",
	strokeWidth = 0,
	className,
	style,
	"aria-label": ariaLabel,
}: ShapeSvgProps): React.JSX.Element {
	const startPolygon = useMemo(() => resolveShape(shape), [shape]);
	const endPolygon = useMemo(
		() => (morphTo ? resolveShape(morphTo) : null),
		[morphTo],
	);
	const morph = useMemo(
		() => (endPolygon ? new Morph(startPolygon, endPolygon) : null),
		[startPolygon, endPolygon],
	);

	const pathData = useMemo(() => {
		const cubics = morph ? morph.asCubics(progress) : startPolygon.cubics;
		return toSvgPath(cubics, width, height);
	}, [morph, progress, startPolygon, width, height]);

	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox={`0 0 ${width} ${height}`}
			width={width}
			height={height}
			className={className}
			style={style}
			aria-label={ariaLabel}
			role={ariaLabel ? "img" : "presentation"}
			aria-hidden={!ariaLabel}
		>
			<path
				d={pathData}
				fill={fill}
				stroke={stroke}
				strokeWidth={strokeWidth}
			/>
		</svg>
	);
}
